import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DropdownMenuProps {
  children: React.ReactNode;
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

interface DropdownMenuSeparatorProps {
  className?: string;
}

const DropdownMenuContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}>({
  isOpen: false,
  setIsOpen: () => {},
  triggerRef: { current: null },
});

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen, triggerRef }}>
      <div className="relative inline-block text-left">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
};

export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({
  children,
  asChild = false,
}) => {
  const { isOpen, setIsOpen, triggerRef } = React.useContext(DropdownMenuContext);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      ref: triggerRef,
      onClick: (e: React.MouseEvent) => {
        handleClick(e);
        (children as React.ReactElement<any>).props.onClick?.(e);
      },
      style: { ...((children as React.ReactElement<any>).props.style || {}), cursor: 'pointer' },
    });
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={handleClick}
      className="inline-flex items-center justify-center rounded-md p-2 text-[var(--brand-text-tertiary)] hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-text-secondary)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--brand-focus-ring)]"
    >
      {children}
    </button>
  );
};

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({ 
  children, 
  align = 'end',
  side = 'bottom' 
}) => {
  const { isOpen, setIsOpen, triggerRef } = React.useContext(DropdownMenuContext);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position based on trigger button
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      let top = rect.bottom + scrollTop;
      let left = rect.left + scrollLeft;

      // Adjust positioning based on align prop
      if (align === 'end') {
        left = rect.right + scrollLeft;
      } else if (align === 'center') {
        left = rect.left + scrollLeft + (rect.width / 2);
      }

      // Adjust for side prop
      if (side === 'top') {
        top = rect.top + scrollTop;
      } else if (side === 'right') {
        top = rect.top + scrollTop;
        left = rect.right + scrollLeft;
      } else if (side === 'left') {
        top = rect.top + scrollTop;
        left = rect.left + scrollLeft;
      }

      setPosition({ top, left });
    }
  }, [isOpen, align, side, triggerRef]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(event.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, setIsOpen, triggerRef]);

  if (!isOpen) return null;

  const getTransformClasses = () => {
    let transform = '';
    if (align === 'end') {
      transform += 'translate-x-[-100%] ';
    } else if (align === 'center') {
      transform += 'translate-x-[-50%] ';
    }
    
    if (side === 'top') {
      transform += 'translate-y-[-100%] ';
    }
    
    return transform.trim();
  };

  const dropdownElement = (
    <div
      ref={contentRef}
      className={`fixed z-[9999] min-w-[12rem] rounded-md border bg-[var(--brand-surface-card)] p-1 text-[var(--brand-text-primary)] shadow-xl drop-shadow-lg border-[var(--brand-border)] ${getTransformClasses()}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {children}
    </div>
  );

  // Portal to document.body to escape any overflow constraints
  return createPortal(dropdownElement, document.body);
};

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({ 
  children, 
  onClick, 
  disabled = false,
  className 
}) => {
  const { setIsOpen } = React.useContext(DropdownMenuContext);

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
      setIsOpen(false);
    }
  };

  const classNames = [
    'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
    disabled
      ? 'pointer-events-none opacity-50'
      : 'hover:bg-[var(--brand-surface-hover)] focus:bg-[var(--brand-surface-hover)]',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      onClick={handleClick}
      className={classNames}
    >
      {children}
    </div>
  );
};

export const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({ 
  className 
}) => {
  const classNames = [
    '-mx-1 my-1 h-px bg-[var(--brand-border)]',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames} />
  );
}; 
